import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import AuthImage from "@/components/AuthImage";
import BenchmarkEditor from "@/components/BenchmarkEditor";
import { useCategories } from "@/hooks/useCategories";
import { PlusCircle, PencilSimple, Trash, UploadSimple, Package, MagnifyingGlass, TrashSimple, FileText, FilePdf, QrCode } from "@phosphor-icons/react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { formatDate } from "@/lib/format";
import { generateQrSign } from "@/lib/qrSign";
import { toast } from "sonner";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // product being edited
  const { categories, labelFor } = useCategories();

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { include_inactive: true };
      if (categoryFilter !== "all") params.category = categoryFilter;
      const res = await api.get("/products", { params });
      setProducts(res.data);
    } catch (e) {
      console.error("[AdminProducts] fetch failed:", e);
    }
    setLoading(false);
  }, [categoryFilter]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const filtered = products.filter((p) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (p.name || "").toLowerCase().includes(s) || (p.brand || "").toLowerCase().includes(s);
  });

  const remove = async (p) => {
    if (!window.confirm(`Deactivate "${p.name}"? Clients won't see it anymore.`)) return;
    try {
      await api.delete(`/products/${p.product_id}`);
      toast.success("Product deactivated");
      fetchProducts();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const hardDelete = async (p) => {
    if (!window.confirm(`Permanently delete "${p.name}" and its image? This can't be undone.`)) return;
    try {
      await api.delete(`/products/${p.product_id}`, { params: { hard: true } });
      toast.success("Product deleted");
      fetchProducts();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const reactivate = async (p) => {
    try {
      await api.patch(`/products/${p.product_id}`, { is_active: true });
      toast.success("Product reactivated");
      fetchProducts();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <Layout>
      <div className="mb-8">
        <div className="mt-tiny text-[#FF5A1F]">Factory Console · Products</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight text-zinc-900 mt-2">
          Product catalog
        </h1>
        <p className="mt-2 text-zinc-600 text-sm max-w-2xl">
          One card per material — name, brand, and a single representative image. Sizes and finishes are collected per order.
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-6 md:items-center">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            data-testid="products-search-input"
            placeholder="Search name or brand..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white border border-zinc-200 focus:border-zinc-900 outline-none transition-colors"
          />
        </div>
        <div className="w-full md:w-64">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger data-testid="products-category-filter" className="h-12 border-zinc-200 rounded-none bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <button
          onClick={() => setCreating(true)}
          data-testid="admin-new-product-button"
          className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#E04814] text-white px-5 py-3 font-medium transition-colors"
        >
          <PlusCircle size={18} weight="fill" /> New Product
        </button>
      </div>

      <div className="bg-white border border-zinc-200" data-testid="products-table">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-200">
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Image</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Product</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Category</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Added</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Status</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              ["s-1", "s-2", "s-3"].map((k) => (
                <TableRow key={k}><TableCell colSpan={6}><div className="h-14 bg-zinc-100 animate-pulse" /></TableCell></TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-14 text-zinc-500">
                  <Package size={40} weight="duotone" className="mx-auto mb-2 text-zinc-300" />
                  {q || categoryFilter !== "all" ? "No products match." : "No products yet — add your first."}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((p) => (
                <TableRow key={p.product_id} className="hover:bg-zinc-50 border-zinc-200">
                  <TableCell className="w-16">
                    <AuthImage path={p.image_path} alt={p.name} className="w-12 h-12 object-cover border border-zinc-200"
                      fallback={<div className="w-12 h-12 bg-zinc-100 border border-zinc-200" />} />
                  </TableCell>
                  <TableCell>
                    <div className="font-medium text-zinc-900">{p.name}</div>
                    <div className="mt-tiny text-zinc-500 font-mono-mt">{p.brand || "—"}</div>
                  </TableCell>
                  <TableCell className="text-sm">{labelFor(p.category)}</TableCell>
                  <TableCell className="text-sm text-zinc-600">{formatDate(p.created_at)}</TableCell>
                  <TableCell>
                    {p.is_active ? (
                      <span className="text-xs font-semibold text-green-700">ACTIVE</span>
                    ) : (
                      <span className="text-xs font-semibold text-zinc-400">INACTIVE</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => generateQrSign({ productId: p.product_id, productName: p.name })}
                        data-testid={`qr-sign-${p.product_id}`}
                        title="Generate a print-ready 20cm QR sign"
                        className="inline-flex items-center gap-1.5 border border-zinc-200 hover:border-[#0047AB] hover:text-[#0047AB] px-3 py-1.5 text-xs font-medium transition-colors"
                      >
                        <QrCode size={14} /> QR Sign
                      </button>
                      <button
                        onClick={() => setEditing(p)}
                        data-testid={`edit-product-${p.product_id}`}
                        className="inline-flex items-center gap-1.5 border border-zinc-200 hover:border-[#0047AB] hover:text-[#0047AB] px-3 py-1.5 text-xs font-medium transition-colors"
                      >
                        <PencilSimple size={14} /> Edit
                      </button>
                      {p.is_active ? (
                        <button
                          onClick={() => remove(p)}
                          data-testid={`deactivate-product-${p.product_id}`}
                          className="inline-flex items-center gap-1.5 border border-zinc-200 hover:border-amber-500 hover:text-amber-600 px-3 py-1.5 text-xs font-medium transition-colors"
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          onClick={() => reactivate(p)}
                          className="inline-flex items-center gap-1.5 border border-zinc-200 hover:border-green-600 hover:text-green-600 px-3 py-1.5 text-xs font-medium transition-colors"
                        >
                          Reactivate
                        </button>
                      )}
                      <button
                        onClick={() => hardDelete(p)}
                        data-testid={`delete-product-${p.product_id}`}
                        title="Delete permanently"
                        className="inline-flex items-center gap-1.5 border border-zinc-200 hover:border-red-500 hover:text-red-500 px-3 py-1.5 text-xs font-medium transition-colors"
                      >
                        <TrashSimple size={14} />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <NewProductDialog open={creating} onOpenChange={setCreating} onCreated={fetchProducts} categories={categories} />
      <EditProductDialog product={editing} onClose={() => setEditing(null)} onSaved={fetchProducts} categories={categories} allProducts={products} />
    </Layout>
  );
}

function NewProductDialog({ open, onOpenChange, onCreated, categories }) {
  const [name, setName] = useState("");
  const [brand, setBrand] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [leadTime, setLeadTime] = useState("");
  const [sizes, setSizes] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!category && categories.length > 0) setCategory(categories[0].category_id);
  }, [categories, category]);

  const reset = () => {
    setName(""); setBrand(""); setCategory(categories[0]?.category_id || ""); setDescription("");
    setLeadTime(""); setSizes(""); setFile(null); setPreview(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!name.trim() || !file) {
      toast.error("Name and image are required");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("name", name);
      form.append("brand", brand);
      form.append("category", category);
      form.append("description", description);
      form.append("lead_time", leadTime);
      form.append("sizes", sizes);
      form.append("image", file);
      await api.post("/products", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Product added");
      reset();
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to create");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="rounded-none border-zinc-200 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-product-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add new product</DialogTitle>
          <DialogDescription>
            One representative image per product. Colours and sizes are per order.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Image upload */}
          <div>
            <div className="mt-tiny text-zinc-500 mb-1.5">Image *</div>
            <label
              data-testid="new-product-image-label"
              className="block border border-dashed border-zinc-300 hover:border-[#0047AB] hover:bg-zinc-50 aspect-video cursor-pointer transition-colors overflow-hidden"
            >
              {preview ? (
                <img src={preview} alt="preview" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full grid place-items-center">
                  <div className="flex items-center gap-2 text-zinc-500">
                    <UploadSimple size={20} className="text-[#0047AB]" />
                    <span className="text-sm font-medium">Choose image (JPG/PNG)</span>
                  </div>
                </div>
              )}
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                onChange={pickFile}
                className="hidden"
                data-testid="new-product-image-input"
              />
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Name *</div>
              <Input data-testid="new-product-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="18mm HMR Board" className="rounded-none h-11 border-zinc-200" />
            </div>
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Brand</div>
              <Input data-testid="new-product-brand-input" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Century, Greenlam..." className="rounded-none h-11 border-zinc-200" />
            </div>
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Lead time</div>
              <Input data-testid="new-product-leadtime-input" value={leadTime} onChange={(e) => setLeadTime(e.target.value)} placeholder="7–10 days" className="rounded-none h-11 border-zinc-200" />
            </div>
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Available sizes</div>
              <Input data-testid="new-product-sizes-input" value={sizes} onChange={(e) => setSizes(e.target.value)} placeholder="8×4 ft, 6×3 ft, custom" className="rounded-none h-11 border-zinc-200" />
            </div>
          </div>

          <div>
            <div className="mt-tiny text-zinc-500 mb-1.5">Category *</div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="new-product-category-select" className="h-11 rounded-none border-zinc-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                {categories.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="mt-tiny text-zinc-500 mb-1.5">Short description</div>
            <Textarea data-testid="new-product-description-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Moisture-resistant, boiling-water-proof..." className="rounded-none border-zinc-200 min-h-[80px]" />
          </div>
        </div>

        <DialogFooter>
          <button
            onClick={() => onOpenChange(false)}
            className="border border-zinc-200 hover:border-zinc-900 px-4 py-2.5 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="new-product-submit-button"
            className="bg-[#FF5A1F] hover:bg-[#E04814] text-white px-6 py-2.5 font-medium transition-colors disabled:opacity-60"
          >
            {busy ? "Adding..." : "Add product"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EditProductDialog({ product, onClose, onSaved, categories, allProducts = [] }) {
  const [name, setName] = useState("");
  const [brand, setBrand] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [leadTime, setLeadTime] = useState("");
  const [sizes, setSizes] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const inputRef = useRef(null);
  const pdfRef = useRef(null);
  const [pdfKind, setPdfKind] = useState("certificate");
  const [current, setCurrent] = useState(null);
  const [materials, setMaterials] = useState("");
  const [optionSizes, setOptionSizes] = useState("");
  const [finishes, setFinishes] = useState([]);
  const [videos, setVideos] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [compareAgainst, setCompareAgainst] = useState([]);
  const [compareSearch, setCompareSearch] = useState("");
  const [benchmark, setBenchmark] = useState({ rows: [], competitors: [], winners: {} });

  useEffect(() => {
    if (product) {
      setName(product.name || "");
      setBrand(product.brand || "");
      setCategory(product.category || categories[0]?.category_id || "");
      setDescription(product.description || "");
      setLeadTime(product.lead_time || "");
      setSizes(product.sizes || "");
      setMaterials((product.materials || []).join(", "));
      setOptionSizes((product.option_sizes || []).join(", "));
      setFinishes(product.finishes || []);
      const vids = product.videos && product.videos.length
        ? product.videos
        : product.video_url
        ? [{ title: "Factory tour", url: product.video_url }]
        : [];
      setVideos(vids);
      setHighlights(product.highlights || []);
      setCompareAgainst(product.compare_against || []);
      setCompareSearch("");
      setBenchmark(product.benchmark || { rows: [], competitors: [], winners: {} });
      setFile(null);
      setPreview(null);
      setCurrent(product);
    }
  }, [product, categories]);

  const splitList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!product) return;
    if (!name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      // Update text fields
      await api.patch(`/products/${product.product_id}`, {
        name, brand, category, description, lead_time: leadTime, sizes,
        materials: splitList(materials),
        option_sizes: splitList(optionSizes),
        finishes: finishes.filter((f) => (f.name || "").trim()),
        videos: videos
          .filter((v) => (v.url || "").trim())
          .slice(0, 5)
          .map((v) => ({ title: (v.title || "").trim(), url: v.url.trim() })),
        video_url: (videos.find((v) => (v.url || "").trim())?.url || "").trim(),
        highlights: highlights
          .filter((h) => (h.label || "").trim() || (h.value || "").trim())
          .map((h) => ({ label: (h.label || "").trim(), value: (h.value || "").trim() })),
        compare_against: compareAgainst,
        benchmark,
      });
      // If a new image was chosen, replace it
      if (file) {
        const form = new FormData();
        form.append("image", file);
        await api.post(`/products/${product.product_id}/image`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Product updated");
      if (inputRef.current) inputRef.current.value = "";
      onClose();
      onSaved?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to update");
    }
    setBusy(false);
  };

  const uploadPdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !product) return;
    setPdfBusy(true);
    try {
      const form = new FormData();
      form.append("kind", pdfKind);
      form.append("file", f);
      const res = await api.post(`/products/${product.product_id}/pdfs`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("PDF attached");
      if (pdfRef.current) pdfRef.current.value = "";
      // refresh product locally
      const fresh = await api.get(`/products/${product.product_id}`);
      setCurrent(fresh.data);
      onSaved?.();
    } catch (err) { toast.error("Upload failed"); }
    setPdfBusy(false);
  };

  const removePdf = async (fileId) => {
    if (!product) return;
    if (!window.confirm("Remove this PDF?")) return;
    try {
      await api.delete(`/products/${product.product_id}/pdfs/${fileId}`);
      toast.success("Removed");
      const fresh = await api.get(`/products/${product.product_id}`);
      setCurrent(fresh.data);
      onSaved?.();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none border-zinc-200 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-product-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit product</DialogTitle>
          <DialogDescription>
            Update fields. Upload a new image to replace the current one — otherwise the existing image stays.
          </DialogDescription>
        </DialogHeader>

        <button
          onClick={() => generateQrSign({ productId: product?.product_id, productName: name || product?.name })}
          data-testid="edit-generate-qr-sign"
          className="w-full inline-flex items-center justify-center gap-2 bg-zinc-900 text-white hover:bg-[#0047AB] px-4 py-3 text-sm font-semibold transition-colors"
        >
          <QrCode size={18} /> Generate 20cm QR Sign
        </button>

        <div className="space-y-4">
          <div>
            <div className="mt-tiny text-zinc-500 mb-1.5">Image</div>
            <label
              data-testid="edit-product-image-label"
              className="block border border-dashed border-zinc-300 hover:border-[#0047AB] hover:bg-zinc-50 aspect-video cursor-pointer transition-colors overflow-hidden"
            >
              {preview ? (
                <img src={preview} alt="new preview" className="w-full h-full object-cover" />
              ) : product?.image_path ? (
                <AuthImage path={product.image_path} alt={product.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full grid place-items-center text-zinc-500">
                  <UploadSimple size={20} />
                </div>
              )}
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                onChange={pickFile}
                className="hidden"
                data-testid="edit-product-image-input"
              />
            </label>
            <div className="mt-tiny text-zinc-400 mt-1.5">Click above to pick a new image (optional).</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Name *</div>
              <Input data-testid="edit-product-name-input" value={name} onChange={(e) => setName(e.target.value)} className="rounded-none h-11 border-zinc-200" />
            </div>
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Brand</div>
              <Input data-testid="edit-product-brand-input" value={brand} onChange={(e) => setBrand(e.target.value)} className="rounded-none h-11 border-zinc-200" />
            </div>
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Lead time</div>
              <Input data-testid="edit-product-leadtime-input" value={leadTime} onChange={(e) => setLeadTime(e.target.value)} className="rounded-none h-11 border-zinc-200" placeholder="7–10 days" />
            </div>
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Available sizes</div>
              <Input data-testid="edit-product-sizes-input" value={sizes} onChange={(e) => setSizes(e.target.value)} className="rounded-none h-11 border-zinc-200" placeholder="8×4 ft, custom" />
            </div>
          </div>

          <div>
            <div className="mt-tiny text-zinc-500 mb-1.5">Category</div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="edit-product-category-select" className="h-11 rounded-none border-zinc-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                {categories.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="mt-tiny text-zinc-500 mb-1.5">Short description</div>
            <Textarea data-testid="edit-product-description-input" value={description} onChange={(e) => setDescription(e.target.value)} className="rounded-none border-zinc-200 min-h-[80px]" />
          </div>

          {/* Client-facing selection options */}
          <div className="pt-4 border-t border-zinc-200 space-y-3" data-testid="product-options-editor">
            <div className="mt-tiny text-[#FF5A1F]">Selection options</div>
            <p className="text-xs text-zinc-500 -mt-1">
              Shown as pills / swatches on the product page and captured on the quote request.
            </p>
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Materials (comma separated)</div>
              <Input
                data-testid="edit-product-materials-input"
                value={materials}
                onChange={(e) => setMaterials(e.target.value)}
                placeholder="Zinc, Stainless Steel, Brass"
                className="rounded-none h-11 border-zinc-200"
              />
            </div>
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Sizes (comma separated)</div>
              <Input
                data-testid="edit-product-optionsizes-input"
                value={optionSizes}
                onChange={(e) => setOptionSizes(e.target.value)}
                placeholder="150x25mm, 177x28mm"
                className="rounded-none h-11 border-zinc-200"
              />
            </div>
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Finishes</div>
              <div className="space-y-2" data-testid="edit-product-finishes-list">
                {finishes.map((f, i) => (
                  <div key={`fin-${i}`} className="flex items-center gap-2">
                    <input
                      type="color"
                      value={f.hex || "#cccccc"}
                      onChange={(e) =>
                        setFinishes((arr) => arr.map((x, j) => (j === i ? { ...x, hex: e.target.value } : x)))
                      }
                      data-testid={`finish-hex-input-${i}`}
                      className="w-11 h-11 border border-zinc-200 bg-white cursor-pointer p-1"
                    />
                    <Input
                      value={f.name || ""}
                      onChange={(e) =>
                        setFinishes((arr) => arr.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))
                      }
                      placeholder="Dark Antique Brass"
                      data-testid={`finish-name-input-${i}`}
                      className="rounded-none h-11 border-zinc-200 flex-1"
                    />
                    <button
                      onClick={() => setFinishes((arr) => arr.filter((_, j) => j !== i))}
                      data-testid={`finish-remove-${i}`}
                      className="text-red-500 hover:bg-red-50 p-2"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setFinishes((arr) => [...arr, { name: "", hex: "#cccccc" }])}
                data-testid="finish-add-button"
                className="mt-2 inline-flex items-center gap-1.5 border border-zinc-200 hover:border-[#0047AB] hover:text-[#0047AB] px-3 py-1.5 text-xs font-medium transition-colors"
              >
                <PlusCircle size={14} /> Add finish
              </button>
            </div>
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Factory videos (up to 5 YouTube links)</div>
              <div className="space-y-2" data-testid="edit-product-videos-list">
                {videos.map((v, i) => (
                  <div key={`vid-${i}`} className="flex items-center gap-2">
                    <Input
                      value={v.title || ""}
                      onChange={(e) => setVideos((arr) => arr.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))}
                      placeholder="Marble Polishing Process"
                      data-testid={`video-title-input-${i}`}
                      className="rounded-none h-11 border-zinc-200 w-1/3"
                    />
                    <Input
                      value={v.url || ""}
                      onChange={(e) => setVideos((arr) => arr.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))}
                      placeholder="https://www.youtube.com/watch?v=..."
                      data-testid={`video-url-input-${i}`}
                      className="rounded-none h-11 border-zinc-200 flex-1"
                    />
                    <button
                      onClick={() => setVideos((arr) => arr.filter((_, j) => j !== i))}
                      data-testid={`video-remove-${i}`}
                      className="text-red-500 hover:bg-red-50 p-2"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                ))}
              </div>
              {videos.length < 5 && (
                <button
                  onClick={() => setVideos((arr) => [...arr, { title: "", url: "" }])}
                  data-testid="video-add-button"
                  className="mt-2 inline-flex items-center gap-1.5 border border-zinc-200 hover:border-[#0047AB] hover:text-[#0047AB] px-3 py-1.5 text-xs font-medium transition-colors"
                >
                  <PlusCircle size={14} /> Add video
                </button>
              )}
              <div className="mt-tiny text-zinc-400 mt-1.5">
                Shown to clients as the "Factory in Action" gallery. Leave empty to hide the section.
              </div>
            </div>
          </div>

          {/* Product Highlights */}
          <div className="pt-4 border-t border-zinc-200 space-y-3" data-testid="product-highlights-editor">
            <div className="mt-tiny text-[#FF5A1F]">Product Highlights</div>
            <p className="text-xs text-zinc-500 -mt-1">
              Custom label + value rows, shown as a bullet list on the product page
              (e.g. "Grade" : "Moisture Resistant"). Type any label you like.
            </p>
            <div className="space-y-2" data-testid="highlights-list">
              {highlights.map((h, i) => (
                <div key={`hl-${i}`} className="flex items-center gap-2">
                  <Input
                    value={h.label || ""}
                    onChange={(e) =>
                      setHighlights((arr) => arr.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))
                    }
                    placeholder="Label (e.g. Grade)"
                    data-testid={`highlight-label-input-${i}`}
                    className="rounded-none h-11 border-zinc-200 w-1/3"
                  />
                  <Input
                    value={h.value || ""}
                    onChange={(e) =>
                      setHighlights((arr) => arr.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))
                    }
                    placeholder="Value (e.g. Moisture Resistant (MR))"
                    data-testid={`highlight-value-input-${i}`}
                    className="rounded-none h-11 border-zinc-200 flex-1"
                  />
                  <button
                    onClick={() => setHighlights((arr) => arr.filter((_, j) => j !== i))}
                    data-testid={`highlight-remove-${i}`}
                    className="text-red-500 hover:bg-red-50 p-2"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={() => setHighlights((arr) => [...arr, { label: "", value: "" }])}
              data-testid="highlight-add-button"
              className="mt-2 inline-flex items-center gap-1.5 border border-zinc-200 hover:border-[#0047AB] hover:text-[#0047AB] px-3 py-1.5 text-xs font-medium transition-colors"
            >
              <PlusCircle size={14} /> Add highlight
            </button>
          </div>

          {/* Comparison — compare-against products */}
          <div className="pt-4 border-t border-zinc-200 space-y-3" data-testid="product-compare-editor">
            <div className="mt-tiny text-[#FF5A1F]">Comparison scorecard</div>
            <p className="text-xs text-zinc-500 -mt-1">
              Pick up to 3 products to show side-by-side on this product's scorecard. Manage
              categories & scores in Admin → Compare.
            </p>
            <div className="text-xs text-zinc-500">
              Selected: <span className="font-semibold text-zinc-800">{compareAgainst.length}/3</span>
            </div>
            <Input
              value={compareSearch}
              onChange={(e) => setCompareSearch(e.target.value)}
              placeholder="Search products to compare against"
              data-testid="compare-search-input"
              className="rounded-none h-11 border-zinc-200"
            />
            <div className="max-h-52 overflow-y-auto border border-zinc-200 divide-y divide-zinc-100" data-testid="compare-product-list">
              {allProducts
                .filter((op) => op.product_id !== product?.product_id)
                .filter(
                  (op) =>
                    !compareSearch.trim() ||
                    (op.name || "").toLowerCase().includes(compareSearch.toLowerCase()) ||
                    (op.brand || "").toLowerCase().includes(compareSearch.toLowerCase())
                )
                .map((op) => {
                  const checked = compareAgainst.includes(op.product_id);
                  const disabled = !checked && compareAgainst.length >= 3;
                  return (
                    <label
                      key={op.product_id}
                      data-testid={`compare-option-${op.product_id}`}
                      className={`flex items-center gap-3 p-2.5 cursor-pointer transition-colors ${
                        checked ? "bg-[#0047AB]/5" : "hover:bg-zinc-50"
                      } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() =>
                          setCompareAgainst((arr) =>
                            checked ? arr.filter((id) => id !== op.product_id) : [...arr, op.product_id]
                          )
                        }
                        className="accent-[#0047AB] w-4 h-4"
                      />
                      <div className="w-8 h-8 bg-zinc-100 overflow-hidden shrink-0">
                        <AuthImage path={op.image_path} alt={op.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm text-zinc-900 truncate">{op.name}</div>
                        {op.brand && <div className="text-[11px] text-zinc-400 truncate">{op.brand}</div>}
                      </div>
                    </label>
                  );
                })}
              {allProducts.filter((op) => op.product_id !== product?.product_id).length === 0 && (
                <div className="p-3 text-xs text-zinc-400 text-center">No other products yet.</div>
              )}
            </div>
          </div>

          <BenchmarkEditor productId={product?.product_id} value={benchmark} onChange={setBenchmark} />

          {/* Gallery images */}
          {product && (
            <ProductGalleryEditor
              productId={product.product_id}
              gallery={current?.gallery || product.gallery || []}
              onChanged={async () => {
                const fresh = await api.get(`/products/${product.product_id}`);
                setCurrent(fresh.data);
                onSaved?.();
              }}
            />
          )}

          {/* Documents (up to 20) */}
          {product && (
            <ProductDocumentsEditor
              productId={product.product_id}
              initialDocs={current?.documents || product.documents || []}
              onChanged={async () => {
                const fresh = await api.get(`/products/${product.product_id}`);
                setCurrent(fresh.data);
                onSaved?.();
              }}
            />
          )}
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            className="border border-zinc-200 hover:border-zinc-900 px-4 py-2.5 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="edit-product-submit-button"
            className="bg-[#0047AB] hover:bg-[#003380] text-white px-6 py-2.5 font-medium transition-colors disabled:opacity-60"
          >
            {busy ? "Saving..." : "Save changes"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ProductGalleryEditor({ productId, gallery, onChanged }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const items = useMemo(
    () => (gallery || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0)),
    [gallery]
  );

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("image", f);
      await api.post(`/products/${productId}/gallery`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Gallery image added");
      if (ref.current) ref.current.value = "";
      await onChanged?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    }
    setBusy(false);
  };

  const remove = async (imageId) => {
    if (!window.confirm("Remove this gallery image?")) return;
    try {
      await api.delete(`/products/${productId}/gallery/${imageId}`);
      await onChanged?.();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="pt-4 border-t border-zinc-200 space-y-3" data-testid="product-gallery-editor">
      <div className="flex items-baseline justify-between">
        <div className="mt-tiny text-[#FF5A1F]">Gallery images</div>
        <div className="mt-tiny text-zinc-500">{items.length} / 10</div>
      </div>
      <p className="text-xs text-zinc-500 -mt-1">
        Extra views shown as thumbnails next to the main image. The main image always appears first.
      </p>
      {items.length > 0 && (
        <div className="grid grid-cols-4 gap-2" data-testid="gallery-images-list">
          {items.map((g) => (
            <div key={g.image_id} className="relative aspect-square border border-zinc-200 overflow-hidden group">
              <AuthImage path={g.image_path} alt="gallery" className="w-full h-full object-cover" />
              <button
                onClick={() => remove(g.image_id)}
                data-testid={`gallery-remove-${g.image_id}`}
                className="absolute top-1 right-1 bg-white/90 text-red-500 p-1 hover:bg-white"
              >
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
      {items.length < 10 && (
        <label className="block border border-dashed border-zinc-300 hover:border-[#0047AB] px-3 py-3 text-xs text-center cursor-pointer bg-white">
          <UploadSimple size={14} className="inline mr-1 text-[#0047AB]" />
          {busy ? "Uploading..." : "Add gallery image"}
          <input
            ref={ref}
            type="file"
            accept="image/*"
            onChange={upload}
            className="hidden"
            data-testid="gallery-image-input"
          />
        </label>
      )}
    </div>
  );
}

function ProductDocumentsEditor({ productId, initialDocs, onChanged }) {
  const [docs, setDocs] = useState(initialDocs || []);
  const [newName, setNewName] = useState("");
  const [newIcon, setNewIcon] = useState(null);
  const [newPdf, setNewPdf] = useState(null);
  const [busy, setBusy] = useState(false);
  const iconInRef = useRef(null);
  const pdfInRef = useRef(null);

  useEffect(() => { setDocs(initialDocs || []); }, [initialDocs]);

  const sortedDocs = useMemo(
    () => docs.slice().sort((a, b) => (a.order || 0) - (b.order || 0)),
    [docs]
  );

  const refresh = async () => {
    try {
      const res = await api.get(`/products/${productId}`);
      setDocs(res.data.documents || []);
      onChanged?.();
    } catch (e) { console.error("Failed to refresh product documents:", e); }
  };

  const add = async () => {
    if (!newName.trim()) return toast.error("Give the document a name");
    if (docs.length >= 20) return toast.error("Maximum 20 documents per product");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", newName.trim());
      if (newIcon) fd.append("icon", newIcon);
      if (newPdf) fd.append("pdf", newPdf);
      await api.post(`/products/${productId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setNewName(""); setNewIcon(null); setNewPdf(null);
      if (iconInRef.current) iconInRef.current.value = "";
      if (pdfInRef.current) pdfInRef.current.value = "";
      toast.success("Document added");
      await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to add");
    }
    setBusy(false);
  };

  return (
    <div className="pt-4 border-t border-zinc-200 space-y-3" data-testid="product-documents-editor">
      <div className="flex items-baseline justify-between">
        <div className="mt-tiny text-[#FF5A1F]">Documents</div>
        <div className="mt-tiny text-zinc-500">{docs.length} / 20</div>
      </div>
      <p className="text-xs text-zinc-500 -mt-1">
        Upload a custom icon and a PDF for each spec sheet, drawing or certificate. Only slots with a PDF appear on the client's page.
      </p>

      {docs.length > 0 && (
        <div className="space-y-2" data-testid="product-documents-list">
          {sortedDocs.map((d) => (
            <DocRow key={d.doc_id} productId={productId} doc={d} onChanged={refresh} />
          ))}
        </div>
      )}

      {docs.length < 20 && (
        <div className="bg-zinc-50 border border-zinc-200 p-3 space-y-2" data-testid="product-documents-add-form">
          <div className="text-xs font-semibold text-zinc-700">Add a document</div>
          <Input
            placeholder="e.g. Aluminium spec sheet, Ironmongery drawing"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="rounded-none h-10 border-zinc-200 bg-white"
            data-testid="new-doc-name-input"
          />
          <div className="grid grid-cols-2 gap-2">
            <label className="border border-dashed border-zinc-300 hover:border-[#0047AB] px-3 py-2 text-xs text-center cursor-pointer bg-white truncate">
              <UploadSimple size={14} className="inline mr-1 text-[#0047AB]" />
              {newIcon ? newIcon.name : "Icon (optional)"}
              <input
                ref={iconInRef}
                type="file"
                accept="image/*"
                onChange={(e) => setNewIcon(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="new-doc-icon-input"
              />
            </label>
            <label className="border border-dashed border-zinc-300 hover:border-[#0047AB] px-3 py-2 text-xs text-center cursor-pointer bg-white truncate">
              <UploadSimple size={14} className="inline mr-1 text-[#0047AB]" />
              {newPdf ? newPdf.name : "PDF file"}
              <input
                ref={pdfInRef}
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => setNewPdf(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="new-doc-pdf-input"
              />
            </label>
          </div>
          <button
            onClick={add}
            disabled={busy || !newName.trim()}
            data-testid="new-doc-add-button"
            className="w-full bg-[#0047AB] hover:bg-[#003380] text-white text-sm py-2 disabled:opacity-50 transition-colors"
          >
            {busy ? "Adding..." : "Add document"}
          </button>
        </div>
      )}
    </div>
  );
}

function DocRow({ productId, doc, onChanged }) {
  const [name, setName] = useState(doc.name || "");
  const [busy, setBusy] = useState(false);
  const iconRef = useRef(null);
  const pdfRef = useRef(null);

  useEffect(() => { setName(doc.name || ""); }, [doc.name]);

  const patch = async (fd) => {
    setBusy(true);
    try {
      await api.patch(`/products/${productId}/documents/${doc.doc_id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await onChanged?.();
      toast.success("Saved");
    } catch (e) { toast.error("Failed"); }
    setBusy(false);
  };

  const changeIcon = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("icon", f);
    await patch(fd);
    if (iconRef.current) iconRef.current.value = "";
  };
  const changePdf = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("pdf", f);
    await patch(fd);
    if (pdfRef.current) pdfRef.current.value = "";
  };
  const saveName = async () => {
    if (!name.trim() || name === doc.name) return;
    const fd = new FormData(); fd.append("name", name.trim());
    await patch(fd);
  };
  const remove = async () => {
    if (!window.confirm(`Remove "${doc.name}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/products/${productId}/documents/${doc.doc_id}`);
      await onChanged?.();
    } catch (e) { toast.error("Failed"); }
    setBusy(false);
  };

  return (
    <div
      className="border border-zinc-200 p-2 flex items-center gap-3 bg-white"
      data-testid={`doc-row-${doc.doc_id}`}
    >
      <label
        className="w-14 h-14 shrink-0 border border-zinc-200 bg-zinc-50 grid place-items-center cursor-pointer hover:border-[#0047AB] overflow-hidden"
        title="Click to upload / change icon"
      >
        {doc.icon_path ? (
          <AuthImage
            path={doc.icon_path}
            alt={doc.name}
            className="w-full h-full object-contain"
            fallback={<FileText size={22} className="text-zinc-400" />}
          />
        ) : (
          <FileText size={22} className="text-zinc-400" />
        )}
        <input
          ref={iconRef}
          type="file"
          accept="image/*"
          onChange={changeIcon}
          className="hidden"
          data-testid={`doc-icon-input-${doc.doc_id}`}
        />
      </label>
      <div className="flex-1 min-w-0 space-y-1">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={saveName}
          className="rounded-none h-9 border-zinc-200 text-sm"
          data-testid={`doc-name-input-${doc.doc_id}`}
        />
        <label className="flex items-center gap-1.5 text-xs cursor-pointer text-zinc-600 hover:text-[#0047AB] w-fit">
          <FilePdf size={14} className="text-[#B22222]" weight="duotone" />
          <span className="truncate max-w-[220px]">
            {doc.pdf_filename ? doc.pdf_filename : "No PDF — click to attach"}
          </span>
          <span className="text-[#0047AB] underline">
            {doc.pdf_path ? "change" : "attach"}
          </span>
          <input
            ref={pdfRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={changePdf}
            className="hidden"
            data-testid={`doc-pdf-input-${doc.doc_id}`}
          />
        </label>
      </div>
      <button
        onClick={remove}
        disabled={busy}
        title="Remove this document"
        data-testid={`doc-remove-${doc.doc_id}`}
        className="text-red-500 hover:bg-red-50 p-2 disabled:opacity-50"
      >
        <Trash size={16} />
      </button>
    </div>
  );
}
